import React, { createContext, useState, useCallback, useEffect } from 'react';
import { authService } from '../services/authService';

export const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [admin, setAdmin] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Restore admin session from localStorage on app startup
  // Validate token with backend instead of trusting client-side parsing
  useEffect(() => {
    const token = authService.getToken();
    if (token) {
      // Token exists - validate with backend
      const validateToken = async () => {
        try {
          // Make a lightweight API call to verify token is valid
          const response = await authService.validateToken();
          if (response.data.valid) {
            setAdmin({ email: response.data.email || 'admin' });
          } else {
            authService.clearToken();
          }
        } catch (err) {
          // Token validation failed, clear it
          authService.clearToken();
        } finally {
          setLoading(false);
        }
      };
      validateToken();
    } else {
      setLoading(false);
    }
  }, []);

  const login = useCallback(async (email, password) => {
    setLoading(true);
    setError(null);
    try {
      const response = await authService.login(email, password);

      // Check if OTP is required based on backend response
      // Check if OTP is skipped (cache valid) or required
      if (response.data.skipOtp) {
        // OTP verification is skipped, token is returned properly
        authService.setToken(response.data.token);
        setAdmin({ email: email });
        return { success: true, requiresOtp: false };
      } else if (response.data.otpRequired) {
        // OTP verification is enabled, need to verify OTP
        return { success: true, requiresOtp: true, sessionToken: response.data.sessionToken };
      } else {
        // Fallback or legacy response handling
        authService.setToken(response.data.token || response.data.sessionToken);
        setAdmin({ email: email });
        return { success: true, requiresOtp: false };
      }
    } catch (err) {
      const message = err.response?.data?.message || 'Login failed';
      setError(message);
      return { success: false, error: message };
    } finally {
      setLoading(false);
    }
  }, []);

  const verifyOtp = useCallback(async (sessionToken, otp) => {
    setLoading(true);
    setError(null);
    try {
      const response = await authService.verifyOtp(sessionToken, otp);
      authService.setToken(response.data.token);
      setAdmin({ email: sessionToken }); // sessionToken is the email in this case
      return { success: true };
    } catch (err) {
      const message = err.response?.data?.message || 'OTP verification failed';
      setError(message);
      return { success: false, error: message };
    } finally {
      setLoading(false);
    }
  }, []);

  const logout = useCallback(async () => {
    try {
      await authService.logout();
    } catch (err) {
      console.error('Logout error:', err);
    } finally {
      authService.clearToken();
      setAdmin(null);
    }
  }, []);

  const value = React.useMemo(() => ({
    admin,
    loading,
    error,
    login,
    verifyOtp,
    logout,
    isAuthenticated: !!admin,
  }), [admin, loading, error, login, verifyOtp, logout]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
